import { formatDate } from '@angular/common';
import { signal } from '@angular/core';
import { EMPTY, Observable, Subscription, catchError, finalize, tap } from 'rxjs';
import { FieldMetadata, FilterMode, FilterType, MetadataModel, SearchFilter } from 'src/app/usage-api';
import { areArraysEqual } from '../../model/utility';
import { TableColumn, TableColumnRenders } from './datasource';

const DATE_QUERY_FORMAT = 'MM-dd-yyyy';
const DATE_LOCALE = 'en';

const DEFAULT_COLUMN_STYLE: { [key: string]: string } = {
  width: '8rem',
  'min-width': '8rem',
  'max-width': '12rem',
  'white-space': 'nowrap',
  'overflow-wrap': 'break-word',
};

export interface LoadMetadataCallbacks {
  subscription?: (subscription: Subscription) => void;
  next?: (value: MetadataModel) => void;
  error?: (error: unknown) => void;
}

/**
 * Configuration for metadata loading
 */
export interface MetadataConfig<TModel extends object> {
  loadMetadata: () => Observable<MetadataModel>;
  getLocalizationKey: (field: FieldMetadata) => string;
  getColumnStyle?: (field: FieldMetadata) => { [key: string]: string };
  customizeColumn?: (column: TableColumn<TModel>, field: FieldMetadata) => void;
  dateRangeFields?: string[];
  /** Called whenever search filters are updated (e.g. to trigger a data reload). */
  onSearchFiltersChange?: () => void;
  /** Called when metadata/columns have loaded or changed (e.g. to refresh the table column list). */
  onColumnsChanged?: () => void;
}

export class MetadataFilterManager<TModel extends object> {
  readonly metadata = signal<MetadataModel | null>(null);
  readonly isMetadataLoaded = signal(false);
  readonly columnsChanged = signal(false);
  readonly searchFilters = signal<SearchFilter[]>([]);
  readonly isLoading = signal(false);

  private readonly dynamicFilters = new Map<string, ReturnType<typeof signal<string[] | null>>>();
  private columns: TableColumn<TModel>[] = [];
  private readonly subscriptions: Subscription[] = [];

  constructor(private readonly config: MetadataConfig<TModel>) {}

  /**
   * Load metadata from API and build columns. Callbacks allow the caller to
   * track the subscription, react on success, or handle errors.
   */
  public loadMetadata(callbacks?: LoadMetadataCallbacks): void {
    this.isLoading.set(true);
    const sub = this.config
      .loadMetadata()
      .pipe(
        tap((metadata) => {
          this.metadata.set(metadata);
          this.columns = this.createColumnsFromMetadata(metadata);
          this.isMetadataLoaded.set(true);
          this.columnsChanged.set(true);
          callbacks?.next?.(metadata);
          this.config.onColumnsChanged?.();
        }),
        catchError((error: unknown) => {
          console.error('Failed to load metadata:', error);
          callbacks?.error?.(error);
          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe();

    callbacks?.subscription?.(sub);
    this.subscriptions.push(sub);
  }

  /** Current table columns derived from metadata. Returns a shallow copy. */
  public getColumns(): TableColumn<TModel>[] {
    return [...this.columns];
  }

  /** Update (or create) a dynamic filter for the given field. */
  public updateFilter(
    field: string,
    values: string[] | null,
    mode: FilterMode = FilterMode.Includes,
    type: FilterType = FilterType.Values
  ): void {
    let filterSignal = this.dynamicFilters.get(field);
    if (!filterSignal) {
      filterSignal = signal<string[] | null>(null);
      this.dynamicFilters.set(field, filterSignal);
    }
    filterSignal.set(values);
    this.updateSearchFilters(field, values, mode, type);
  }

  /** Current search filters (read-only snapshot). */
  public getSearchFilters(): SearchFilter[] {
    return this.searchFilters();
  }

  /**
   * Convert current filters to query-friendly key-value pairs for URL params.
   * Date-range fields are split into *Start / *End and formatted. Tracked
   * dynamic filter fields that are no longer in filters are set to null.
   */
  public queryFriendlyFilters(filters: SearchFilter[] | null | undefined): Record<string, string | null> {
    const result: Record<string, string | null> = {};
    const dateRangeFields = new Set(this.config.dateRangeFields ?? []);

    for (const filter of filters ?? []) {
      const key = filter.field ?? '';
      if (!key) continue;

      if (dateRangeFields.has(key)) {
        const startVal = filter.values?.[0] ?? '';
        const endVal = filter.values?.[1] ?? '';
        result[`${key}Start`] = this.formatDateForQuery(startVal);
        result[`${key}End`] = this.formatDateForQuery(endVal);
      } else {
        result[key] = filter.values?.join('||') ?? '';
      }
    }

    for (const fieldName of this.dynamicFilters.keys()) {
      if (!filters?.some((f) => f.field === fieldName)) {
        result[fieldName] = null;
      }
    }

    return result;
  }

  private formatDateForQuery(value: string): string {
    if (!value) return '';
    try {
      return formatDate(new Date(value), DATE_QUERY_FORMAT, DATE_LOCALE);
    } catch (error) {
      console.error('Error formatting date:', error);
      return value;
    }
  }

  /** Compare two filter arrays by field, mode, type, and values. */
  public areFiltersEqual(
    filters1?: SearchFilter[] | null,
    filters2?: SearchFilter[] | null,
  ): boolean {
    if (filters1 === filters2) return true;
    if (!filters1 || !filters2 || filters1.length !== filters2.length) return false;
    return filters1.every((f1, i) => {
      const f2 = filters2[i];
      return (
        f2 &&
        f1.field === f2.field &&
        f1.mode === f2.mode &&
        f1.type === f2.type &&
        areArraysEqual(f1.values, f2.values)
      );
    });
  }

  /** Unsubscribe and clear tracked state. Call when the datasource is destroyed. */
  public destroy(): void {
    for (const sub of this.subscriptions) sub.unsubscribe();
    this.subscriptions.length = 0;
    this.dynamicFilters.clear();
  }

  private createColumnsFromMetadata(metadata: MetadataModel): TableColumn<TModel>[] {
    const fields = metadata?.fields;
    if (!Array.isArray(fields) || fields.length === 0) return [];

    const order = (f: FieldMetadata) => f.properties?.displayOrder ?? 999;
    const sorted = [...fields].sort((a, b) => order(a) - order(b));
    return sorted.map((field) => this.createColumnFromField(field));
  }

  private createColumnFromField(field: FieldMetadata): TableColumn<TModel> {
    const fieldKey = field.key ?? '';
    const fieldType = field.type ?? '';
    const style = this.config.getColumnStyle?.(field) ?? { ...DEFAULT_COLUMN_STYLE };

    const column: TableColumn<TModel> = {
      field: fieldKey,
      localizationKey: this.config.getLocalizationKey(field),
      style,
      noSort: field.properties?.sortable !== true,
      hideByDefault: field.properties?.displayOrder === -1,
      filterable: field.properties?.filterable === true,
      render: this.getRenderForType(fieldType, fieldKey),
    };

    this.config.customizeColumn?.(column, field);
    return column;
  }

  private getRenderForType(fieldType: string, fieldKey: string): (row: TModel) => unknown {
    switch (fieldType) {
      case 'date':
        return (row) => TableColumnRenders.Date(row, fieldKey);
      case 'scaled_float':
      case 'double':
        return (row) => TableColumnRenders.Currency(row, fieldKey, 4);
      default:
        return (row) => TableColumnRenders.Reduce(row, fieldKey);
    }
  }

  private updateSearchFilters(field: string, values: string[] | null, mode: FilterMode, type: FilterType): void {
    const current = this.searchFilters();
    const index = current.findIndex((f) => f.field === field);
    const isEmpty = values == null || values.length === 0;

    if (isEmpty) {
      if (index !== -1) {
        this.searchFilters.set(current.filter((_, i) => i !== index));
        this.config.onSearchFiltersChange?.();
      }
      return;
    }

    const nextFilter: SearchFilter = { field, values, mode, type };
    const updated =
      index === -1 ? [...current, nextFilter] : current.map((f, i) => (i === index ? nextFilter : f));
    this.searchFilters.set(updated);
    this.config.onSearchFiltersChange?.();
  }
}
